import { graphqlRequest } from "@/graphql/client";

import { onZillowResponse } from "@/graphql/subscriptions";
import { logDebug } from "@/utils/errorLogger";
import { createAppSyncSubscription, parseGraphqlBody } from "@/store/subscriptionCore/subscriptionApi";
import { PROCESS_STATUS, SHORT_PROCESS_TIMEOUT_MS } from "@/store/subscriptionCore/processConstants";
import { propertyIdToDetailsPath } from "@/utils/propertyUrlUtils";
import { formatAddress, streetLineFromFullAddress } from "@/utils/stringUtils";
import { adaptZillowResponse, getZillowMessageOutcome, ZILLOW_LINK_CONFLICT_STATUS_CODE } from "@/utils/zillowResponseAdapter";

/**
 * @param {unknown} statusCode
 * @returns {boolean}
 */
function isZillowLinkConflictStatusCode(statusCode) {
  return statusCode === ZILLOW_LINK_CONFLICT_STATUS_CODE || String(statusCode) === String(ZILLOW_LINK_CONFLICT_STATUS_CODE);
}

/**
 * @param {unknown} statusCode
 * @returns {boolean}
 */
function isZillowTerminalSuccessStatusCode(statusCode) {
  return statusCode === 200 || statusCode === "200" || isZillowLinkConflictStatusCode(statusCode);
}

/**
 * @param {import("@/utils/zillowResponseAdapter").AdaptedZillowResponse} adapted
 * @param {import("@/utils/zillowResponseAdapter").ZillowMessageOutcome} outcome
 * @returns {string|null}
 */
function pickZillowCtaPropertyId(adapted, outcome) {
  const pickFirstId = (records) => {
    if (!Array.isArray(records)) return null;
    for (const p of records) {
      if (p == null || typeof p !== "object") continue;
      const id = p.id ?? p.property_id ?? p.pk;
      if (id != null) {
        const s = String(id).trim();
        if (s) return s;
      }
    }
    return null;
  };

  const sel = outcome?.propertySelection;
  if (sel === "new") {
    return pickFirstId(adapted.newProperties) ?? pickFirstId(adapted.allProperties);
  }
  if (sel === "existing") {
    return pickFirstId(adapted.existingProperties) ?? pickFirstId(adapted.allProperties);
  }
  return pickFirstId(adapted.allProperties) ?? pickFirstId(adapted.newProperties) ?? pickFirstId(adapted.existingProperties);
}

const ZILLOW_PROCESS_FAILED_MESSAGE = "Zillow process failed";

/**
 * @param {import("@/utils/zillowResponseAdapter").ZillowMessageOutcome} outcome
 * @param {boolean} isLinkConflict
 * @returns {"success" | "warning" | "info"}
 */
function resolveZillowNotificationOutcome(outcome, isLinkConflict) {
  if (isLinkConflict) return "warning";
  if (outcome?.toastVariant === "warning") return "warning";
  if (outcome?.toastVariant === "info") return "info";
  return "success";
}

/**
 * Duplicate / conflict outcomes must not deep-link to the property (may belong to another user).
 * @param {import("@/utils/zillowResponseAdapter").ZillowMessageOutcome} outcome
 * @param {string|null} ctaPropertyId
 * @returns {string|null}
 */
function resolveZillowNotificationActionPath(outcome, ctaPropertyId) {
  if (!ctaPropertyId) return null;
  if (outcome?.shouldRegisterReportsWatcher === false) return null;
  return propertyIdToDetailsPath(ctaPropertyId);
}

/**
 * @param {unknown} records
 * @returns {string}
 */
function pickFirstFullAddress(records) {
  if (!Array.isArray(records)) return "";
  for (const row of records) {
    if (row == null || typeof row !== "object") continue;
    const raw = typeof row.full_address === "string" ? row.full_address.trim() : "";
    if (raw) return raw;
  }
  return "";
}

/**
 * @param {import("@/utils/zillowResponseAdapter").ZillowMessageOutcome} outcome
 * @param {import("@/utils/zillowResponseAdapter").AdaptedZillowResponse} adapted
 * @returns {string}
 */
function buildZillowNotificationMessage(outcome, adapted) {
  const base = outcome?.toastMessage || "Zillow process completed successfully.";
  if (outcome?.propertySelection !== "existing") return base;

  const rawAddress =
    pickFirstFullAddress(adapted?.existingProperties) || pickFirstFullAddress(adapted?.allProperties);
  if (!rawAddress) return base;

  const streetLabel = formatAddress(streetLineFromFullAddress(rawAddress), false);
  if (!streetLabel) return base;

  return `${base} — ${streetLabel}`;
}

/**
 * @param {string|Object|null|undefined} body
 * @returns {string}
 */
function getZillowSubscriptionErrorMessage(body) {
  try {
    const message = parseGraphqlBody(body)?.message;
    if (typeof message === "string" && message.trim()) return message.trim();
  } catch {
    // Backend may omit body or send non-JSON on rare infra failures.
  }
  return ZILLOW_PROCESS_FAILED_MESSAGE;
}

/**
 * Runs `getZillowProperty` once and subscribes to `onZillowResponse` for the returned id.
 *
 * ASSUMPTION: `params.query` is the full GraphQL operation document built by the caller so its
 * variable definitions match `params.variables` (optional zillow_urls vs addresses, referrer_*, etc.).
 * AppSync requires that alignment; a single static query in this module cannot replace per-request
 * dynamic documents used by AddComparableProperty and AddPropertyModal.
 *
 * @param {Object} params
 * @param {string} params.query - Full `query GetZillowProperty(...) { getZillowProperty(...) { ... } }` string
 * @param {Object} params.variables
 * @param {number} [params.timeoutMs] - Optional subscription timeout override in milliseconds.
 * @param {Function} [params.onDone]
 * @param {Object} context
 * @returns {Promise<Object>}
 */
export async function runZillowProcess(params, context) {
  const { query, variables, onDone, timeoutMs } = params;
  if (typeof query !== "string" || query.trim().length === 0) {
    throw new Error(
      "getZillowProperty query document is required: pass the caller-built dynamic query so variables match the operation."
    );
  }
  context.setProgress({ status: PROCESS_STATUS.INVOKING, message: "Starting Zillow process..." });

  const response = await graphqlRequest({
    query,
    variables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });
  const invokeResult = response?.data?.getZillowProperty;
  if (invokeResult?.statusCode !== "200" && invokeResult?.statusCode !== 200) {
    throw new Error("Failed to start Zillow process");
  }
  const correlationId = invokeResult?.id;
  if (!correlationId) {
    throw new Error("No response id returned from Zillow process");
  }
  context.setCorrelationId(correlationId);
  context.setProgress({ status: PROCESS_STATUS.PROCESSING, message: "Processing Zillow response..." });

  return new Promise((resolve, reject) => {
    let settled = false;
    let teardown = () => {};
    const settleReject = (error) => {
      if (settled) return;
      settled = true;
      teardown();
      reject(error);
    };
    const settleResolve = (value) => {
      if (settled) return;
      settled = true;
      teardown();
      resolve(value);
    };

    const setup = createAppSyncSubscription({
      subscriptionQuery: onZillowResponse,
      correlationId,
      timeoutMs: Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : SHORT_PROCESS_TIMEOUT_MS,
      timeoutErrorMessage: "Zillow process timeout",
      processTag: "Zillow",
      onEvent: (data) => {
        if (settled) return;
        if (!data || data.id !== correlationId) return;
        if (data.status === "processing") return;
        if (data.status === "error" || !isZillowTerminalSuccessStatusCode(data.statusCode)) {
          settleReject(new Error(getZillowSubscriptionErrorMessage(data.body)));
          return;
        }

        const subscriptionStatusCode = data.statusCode;

        const finalize = async () => {
          let rawBody;
          try {
            rawBody = parseGraphqlBody(data.body);
          } catch (error) {
            throw new Error(
              error?.message === "Invalid JSON in GraphQL subscription body"
                ? "Invalid JSON in Zillow subscription body"
                : "Invalid Zillow subscription payload"
            );
          }

          const adapted = adaptZillowResponse(rawBody);
          const outcome = getZillowMessageOutcome(adapted.message);
          const isLinkConflict = adapted.hasLinkConflict || isZillowLinkConflictStatusCode(subscriptionStatusCode);
          const resolvedStatusCode = isLinkConflict ? ZILLOW_LINK_CONFLICT_STATUS_CODE : outcome.statusCode;
          if (typeof onDone === "function") {
            await onDone({ rawBody, adapted, outcome, correlationId, statusCode: resolvedStatusCode });
          }
          const subjectPid =
            context.propertyId != null && String(context.propertyId).trim() ? String(context.propertyId).trim() : null;
          const extractedPid = pickZillowCtaPropertyId(adapted, outcome);
          const ctaPropertyId = subjectPid ?? extractedPid;
          const notificationOutcome = resolveZillowNotificationOutcome(outcome, isLinkConflict);
          const actionPath = resolveZillowNotificationActionPath(outcome, ctaPropertyId);
          settleResolve({
            correlationId,
            statusCode: resolvedStatusCode,
            payload: { rawBody, adapted, outcome, statusCode: resolvedStatusCode },
            notification: {
              outcome: notificationOutcome,
              message: buildZillowNotificationMessage(outcome, adapted),
              ...(actionPath ? { actionRoute: actionPath, actionLabel: "View Property" } : {}),
            },
          });
        };

        void finalize().catch((error) => {
          settleReject(error);
        });
      },
      onError: (error) => {
        settleReject(error);
      },
    });
    teardown = setup.teardown;
    context.registerTeardown(teardown);
  });
}
